import type { PickupTransportationMode } from '../../pickup/models/pickup.model';

/**
 * Destination bucket for locator search.
 * Wire format is `DOM` | `INT` | `USA` | `BULK` (locator Lambda).
 * `INTL` is accepted on the UI (same as pickup) and normalized to `INT` before POST.
 */
export type LocatorDestinationCode = 'DOM' | 'INT' | 'INTL' | 'USA' | 'BULK';

/** Destination codes the locator Lambda accepts on the wire (no `INTL`). */
export type LocatorWireDestinationCode = Exclude<LocatorDestinationCode, 'INTL'>;

/**
 * Maps UI/pickup destination codes to the locator wire enum (`INTL` → `INT`).
 */
export function toLocatorWireDestinationCode(
  destinationCode: LocatorDestinationCode,
): LocatorWireDestinationCode {
  return destinationCode === 'INTL' ? 'INT' : destinationCode;
}

/**
 * Request body for `POST /locator/search`.
 * `postalCode` must have spaces stripped before sending (enforced by `LocatorApiService`).
 */
export interface LocatorSearchRequest {
  postalCode: string;
  destinationCode: LocatorDestinationCode;
  transportationMode: PickupTransportationMode;
}

/** Weekday / weekend day codes returned by the locator Lambda. */
export type LocatorHoursDay = 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun';

/** One hours-of-operation row from `POST /locator/search`. */
export interface LocatorHoursOfOperation {
  day: LocatorHoursDay;
  open: string;
  close: string;
}

/** Weekday latest drop-off product entry from the locator Lambda. */
export interface LocatorDropOff {
  dayType: 'WeekDay';
  shippingProduct: string;
  time: string;
}

/** Structured address for a drop-off/service location returned by the locator. */
export interface LocatorLocationAddress {
  streetNumber: string;
  streetName: string;
  streetType: string;
  municipalityName: string;
  provinceCode: string;
  postalCode: string;
  countryCode: string;
}

/**
 * One service location from `POST /locator/search` (live BE `LocatorLocation` shape).
 * The Service step displays `locations[0]` as the nearest active result.
 */
export interface DropOffLocation {
  locationName: string;
  locationType: string;
  latitude: string;
  longitude: string;
  radialDistanceInKM: string;
  phoneNumber: string;
  specialInstructionsEn: string;
  specialInstructionsFr: string;
  address: LocatorLocationAddress;
  hoursOfOperation: LocatorHoursOfOperation[];
  dropOffs: LocatorDropOff[];
  /** Available Services capabilities (booleans from locator Lambda). */
  labelFreeService: boolean;
  dropBoxesOnsitePurolator: boolean;
  kiosk: boolean;
  packageFreeService: boolean;
  additionalHandling: boolean;
  outboundShippingPrepaid: boolean;
  outboundShippingPayCounter: boolean;
  streetAccess: boolean;
  parcelLockersOnsitePurolator: boolean;
  holdForPickup: boolean;
  dangerousGoods: boolean;
  heavyweight: boolean;
  wheelChairAccess: boolean;
  parkingAccess: boolean;
  /** SF spelling kept as-is on the public API. */
  suppliesavailable: boolean;
}

/** Response body for `POST /locator/search`. */
export interface LocatorSearchResponse {
  locations: DropOffLocation[];
}
